import React from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { useProducts } from '../../context/ProductsContext';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatNumber } from '../../utils/calculations';

const ExecutiveReport = () => {
  const { results, inputs } = useCalculator();
  const { products } = useProducts();
  const { currentUser } = useAuth();

  if (!results) {
    return <div className="text-center py-8 text-gray-600">No data available for report generation.</div>;
  }

  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const isHospital = inputs.facilityType === 'hospital';
  const enabledProducts = products.filter(p => p.enabled !== false);

  return (
    <div className="max-w-5xl mx-auto bg-white p-10 shadow-xl rounded-lg" id="executive-report">
      {/* Header */}
      <div className="border-b-4 border-blue-600 pb-6 mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          EHS Staffing Justification Report
        </h1>
        <p className="text-lg text-gray-600">
          Risk-Based FTE Requirements Analysis
        </p>
        <div className="mt-4 text-sm text-gray-600">
          <p>Prepared: {today}</p>
          <p>Prepared by: {currentUser?.email}</p>
          <p>Facility Type: {isHospital ? '🏥 Academic Hospital' : '🏭 General Industry'}</p>
        </div>
      </div>

      {/* Executive Summary */}
      <section className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-blue-600 pl-4">
          Executive Summary
        </h2>
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Required FTE</p>
              <p className="text-4xl font-bold text-blue-600">{results.totalFTEs.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Annual Investment</p>
              <p className="text-4xl font-bold text-green-700">{formatCurrency(results.totalCost)}</p>
            </div>
          </div>
        </div>
        
        <div className="prose max-w-none leading-relaxed text-gray-700">
          <p className="mb-4">
            Based on a comprehensive risk-based workload analysis, this organization requires <strong>{results.totalFTEs.toFixed(2)} Full-Time Equivalents (FTE)</strong> for Environmental Health & Safety (EHS) operations. This staffing level is justified through a data-driven methodology that accounts for workload complexity, regulatory requirements, organizational risk profile, and industry benchmarks.
          </p>
          <p className="mb-4">
            The recommended staffing represents a total annual investment of <strong>{formatCurrency(results.totalCost)}</strong> (including salary and benefits), which is essential to maintain regulatory compliance, protect employee safety, minimize environmental liability, and support operational continuity.
          </p>
          {isHospital && results.benchmarkFTE > 0 && (
            <p className="mb-4">
              <strong>Industry Validation:</strong> The calculated FTE requirement of {results.totalFTEs.toFixed(2)} is {results.benchmarkStatus === 'good' ? 'aligned with' : results.benchmarkStatus === 'warning' ? 'moderately above' : 'significantly different from'} the industry benchmark of {results.benchmarkFTE.toFixed(2)} FTE (variance: {(results.benchmarkVariance * 100).toFixed(1)}%). This {results.benchmarkStatus === 'good' ? 'confirms' : 'reflects'} the organization's specific risk profile and workload requirements.
            </p>
          )}
        </div>
      </section>

      {/* Methodology */}
      <section className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
          Calculation Methodology
        </h2>
        <div className="bg-gray-50 p-6 rounded-lg leading-relaxed text-gray-700">
          <p className="mb-4">
            This analysis employs a <strong>risk-based activity workload model</strong> that calculates FTE requirements based on:
          </p>
          <ol className="list-decimal ml-6 space-y-3 mb-6">
            <li><strong>Net Available Hours:</strong> Annual work hours adjusted for leave and administrative time to determine productive capacity per FTE</li>
            <li><strong>Activity-Based Workload:</strong> Individual EHS activities weighted by complexity, risk level, and environmental factors</li>
            <li><strong>Core Program Management:</strong> Base hours for program oversight, strategic planning, and continuous improvement</li>
            <li><strong>Logistical Support:</strong> Administrative functions, training coordination, and communication</li>
            {isHospital && (
              <>
                <li><strong>Fire Safety & Life Safety Code Compliance:</strong> Hospital-specific inspection, testing, training, and regulatory requirements</li>
                <li><strong>Environmental Compliance:</strong> Medical waste, wastewater, hazardous materials, and air quality management</li>
              </>
            )}
            <li><strong>Population Scaling:</strong> Workload adjusted based on employee population{isHospital && ' and patient bed capacity'}</li>
          </ol>

          <div className="bg-white p-5 rounded border-2 border-gray-300">
            <p className="font-semibold text-gray-900 mb-3">Formula Summary:</p>
            <div className="font-mono text-sm bg-gray-100 p-4 rounded overflow-x-auto">
              <p className="mb-2">FTE = Total Workload Hours / Productive Hours per FTE</p>
              <p className="mb-2">Where:</p>
              <ul className="ml-4 space-y-1">
                <li>• Productive Hours = (Annual Hours - Leave) × (1 - Admin %)</li>
                <li>• Total Workload = Core + (Activities × Scaling Factor) + Logistics{isHospital && ' + Fire Safety + Environmental'}</li>
                <li>• Activity Hours = Base Hours × (1 + Complexity% + Risk% + Environment%)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Calculations */}
      <section className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-purple-600 pl-4">
          Workload Analysis
        </h2>
        
        <div className="space-y-6">
          {/* Productive Hours */}
          <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
            <h3 className="font-bold text-lg text-gray-900 mb-3">1. Net Productive Hours per FTE</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Annual Work Hours</p>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(inputs.totalHours)}</p>
              </div>
              <div>
                <p className="text-gray-600">Less: Leave Hours</p>
                <p className="text-2xl font-bold text-red-600">-{formatNumber(inputs.leaveHours)}</p>
              </div>
              <div>
                <p className="text-gray-600">Less: Admin Time ({inputs.adminPercent}%)</p>
                <p className="text-2xl font-bold text-red-600">-{formatNumber(results.adminHours)}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t-2 border-green-500">
              <p className="text-gray-600 text-sm">Net Productive Hours per FTE</p>
              <p className="text-3xl font-bold text-green-700">{formatNumber(results.productiveHoursFTE)} hours/year</p>
            </div>
          </div>

          {/* Total Workload */}
          <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
            <h3 className="font-bold text-lg text-gray-900 mb-3">2. Total Annual Workload Requirements</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-700">Core Program Hours</span>
                <span className="font-bold text-gray-900">{formatNumber(inputs.coreHours)} hrs</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-700">Activity-Based Hours (scaled)</span>
                <span className="font-bold text-gray-900">{formatNumber(results.scaledActivityHours)} hrs</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-700">Logistical Support Hours</span>
                <span className="font-bold text-gray-900">{formatNumber(inputs.logisticsHours)} hrs</span>
              </div>
              {isHospital && results.fireSafetyHours > 0 && (
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">🔥 Fire Safety Hours</span>
                  <span className="font-bold text-orange-700">{formatNumber(results.fireSafetyHours)} hrs</span>
                </div>
              )}
              {isHospital && results.environmentalHours > 0 && (
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">🌱 Environmental Compliance Hours</span>
                  <span className="font-bold text-green-700">{formatNumber(results.environmentalHours)} hrs</span>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t-2 border-blue-500">
              <p className="text-gray-600 text-sm">Total Annual Workload</p>
              <p className="text-3xl font-bold text-blue-600">{formatNumber(results.totalWorkloadHours)} hours/year</p>
            </div>
          </div>

          {/* Activity Detail */}
          <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
            <h3 className="font-bold text-lg text-gray-900 mb-3">3. Activity-Based Workload Detail</h3>
            <p className="text-sm text-gray-600 mb-4">
              {enabledProducts.length} active EHS activities with risk-weighted complexity factors
            </p>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-semibold text-gray-700">Activity</th>
                    <th className="text-right p-2 font-semibold text-gray-700">Base Hrs</th>
                    <th className="text-right p-2 font-semibold text-gray-700">Risk %</th>
                    <th className="text-right p-2 font-semibold text-gray-700">Weighted Hrs</th>
                  </tr>
                </thead>
                <tbody>
                  {enabledProducts.map((product, idx) => {
                    const weighted = product.baseHours * (1 + (product.comp / 100) + (product.risk / 100) + (product.env / 100));
                    return (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-2 text-gray-700">{product.name}</td>
                        <td className="p-2 text-right text-gray-600">{product.baseHours}</td>
                        <td className="p-2 text-right text-gray-600">{product.risk}%</td>
                        <td className="p-2 text-right font-semibold text-gray-900">{Math.round(weighted)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-3 border-t bg-gray-50 p-3 rounded">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Subtotal (unscaled):</span>
                <span className="font-bold text-gray-900">{formatNumber(results.totalActivityHours)} hrs</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-gray-700">Scaling Factor (based on {formatNumber(inputs.employeeCount)} employees):</span>
                <span className="font-bold text-blue-600">{results.employeeScalingFactor.toFixed(2)}x</span>
              </div>
              <div className="flex justify-between text-sm mt-2 pt-2 border-t-2 border-blue-500">
                <span className="text-gray-700 font-semibold">Scaled Activity Hours:</span>
                <span className="font-bold text-blue-600 text-lg">{formatNumber(results.scaledActivityHours)} hrs</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Justification */}
      <section className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-orange-600 pl-4">
          Business Justification
        </h2>
        <div className="prose max-w-none leading-relaxed text-gray-700 space-y-4">
          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Regulatory Compliance & Risk Management</h3>
          <p>
            The calculated FTE requirement ensures adequate staffing to maintain compliance with federal, state, and local regulations including OSHA standards, EPA requirements{isHospital && ', Joint Commission standards, CMS conditions of participation, and NFPA Life Safety Code'}. Insufficient EHS staffing creates significant organizational risk:
          </p>
          <ul className="list-disc ml-6 space-y-2">
            <li><strong>Regulatory Penalties:</strong> Non-compliance can result in citations, fines, and increased inspection frequency</li>
            <li><strong>Legal Liability:</strong> Inadequate safety programs increase exposure to workers' compensation claims and litigation</li>
            <li><strong>Operational Disruptions:</strong> Safety incidents can halt operations, delaying critical business functions</li>
            {isHospital && (
              <>
                <li><strong>Accreditation Risk:</strong> Joint Commission findings can jeopardize hospital accreditation and Medicare reimbursement</li>
                <li><strong>Patient Safety:</strong> Environmental and fire safety lapses directly impact patient care quality and safety</li>
              </>
            )}
            <li><strong>Reputational Damage:</strong> Public safety incidents negatively impact organizational reputation and stakeholder confidence</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Cost-Benefit Analysis</h3>
          <p>
            The annual investment of <strong>{formatCurrency(results.totalCost)}</strong> for {results.totalFTEs.toFixed(2)} FTE provides substantial value through risk mitigation and operational efficiency:
          </p>
          <div className="bg-green-50 border-2 border-green-200 p-5 rounded-lg my-4">
            <p className="font-semibold text-gray-900 mb-2">Return on Investment Considerations:</p>
            <ul className="list-disc ml-6 space-y-2 text-sm">
              <li>Average OSHA violation fine: $7,000 - $15,000 per serious violation</li>
              <li>Average workers' compensation claim: $40,000 - $100,000 per incident</li>
              {isHospital && <li>Joint Commission findings can delay reimbursement or require costly remediation</li>}
              <li>Incident-related downtime costs can exceed $500,000 for major events</li>
              <li>Proactive EHS management reduces insurance premiums and claim frequency</li>
            </ul>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Organizational Capacity & Workload</h3>
          <p>
            With an employee population of <strong>{formatNumber(inputs.employeeCount)}</strong>{isHospital && results.inputs?.patientBeds > 0 ? ` and ${formatNumber(inputs.patientBeds)} patient beds` : ''}, the organization's scale necessitates comprehensive EHS coverage. The calculated workload of <strong>{formatNumber(results.totalWorkloadHours)}</strong> annual hours accounts for:
          </p>
          <ul className="list-disc ml-6 space-y-2">
            <li><strong>{enabledProducts.length} Active EHS Programs</strong> requiring regular monitoring, auditing, and improvement</li>
            <li><strong>Average Risk Level: {results.avgRisk.toFixed(1)}%</strong> requiring enhanced oversight and controls</li>
            <li><strong>Productivity Factor: {results.utilization.toFixed(1)}%</strong> accounting for realistic productive capacity after administrative time and leave</li>
            {isHospital && results.fireSafetyHours > 0 && (
              <li><strong>Fire Safety Program: {formatNumber(results.fireSafetyHours)} hours annually</strong> for inspections, drills, training, and Life Safety Code compliance</li>
            )}
            {isHospital && results.environmentalHours > 0 && (
              <li><strong>Environmental Compliance: {formatNumber(results.environmentalHours)} hours annually</strong> for waste management, wastewater, air quality, and hazardous materials</li>
            )}
          </ul>

          {isHospital && results.benchmarkFTE > 0 && (
            <>
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Industry Benchmark Comparison</h3>
              <p>
                The calculated FTE requirement has been validated against healthcare industry standards:
              </p>
              <div className="bg-purple-50 border-2 border-purple-200 p-5 rounded-lg my-4">
                <div className="grid grid-cols-3 gap-4 text-center mb-4">
                  <div>
                    <p className="text-sm text-gray-600">Calculated FTE</p>
                    <p className="text-2xl font-bold text-blue-600">{results.totalFTEs.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Industry Benchmark</p>
                    <p className="text-2xl font-bold text-purple-600">{results.benchmarkFTE.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Variance</p>
                    <p className={`text-2xl font-bold ${results.benchmarkStatus === 'good' ? 'text-green-700' : 'text-orange-700'}`}>
                      {(results.benchmarkVariance * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-700">
                  <strong>Benchmark Methodology:</strong> Based on established ratios of 1 FTE per 300-400 employees and 1 FTE per 250-300 hospital beds, adjusted for facility size and complexity.
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Risks of Understaffing */}
      <section className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-red-600 pl-4">
          Risks of Understaffing
        </h2>
        <div className="bg-red-50 border-2 border-red-200 p-6 rounded-lg">
          <p className="text-gray-700 mb-4 leading-relaxed">
            Staffing below the calculated requirement creates significant organizational vulnerabilities:
          </p>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="text-red-600 font-bold mr-3 text-lg">⚠️</span>
              <div>
                <strong>Reactive vs. Proactive Management:</strong> Insufficient capacity forces reactive incident response rather than proactive hazard prevention, increasing incident frequency and severity
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-red-600 font-bold mr-3 text-lg">⚠️</span>
              <div>
                <strong>Compliance Gaps:</strong> Critical inspections, audits, training, and documentation requirements may be delayed or omitted, creating regulatory exposure
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-red-600 font-bold mr-3 text-lg">⚠️</span>
              <div>
                <strong>Employee Burnout:</strong> Overloaded EHS staff experience reduced effectiveness, higher turnover, and inability to sustain program quality
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-red-600 font-bold mr-3 text-lg">⚠️</span>
              <div>
                <strong>Program Degradation:</strong> Continuous improvement initiatives, employee engagement, and management system updates are deprioritized
              </div>
            </li>
            {isHospital && (
              <li className="flex items-start">
                <span className="text-red-600 font-bold mr-3 text-lg">⚠️</span>
                <div>
                  <strong>Accreditation Jeopardy:</strong> Inadequate EHS oversight increases risk of Joint Commission findings, CMS deficiencies, and potential loss of accreditation or reimbursement eligibility
                </div>
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* Recommendation */}
      <section className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-4">
          Recommendation
        </h2>
        <div className="bg-green-50 border-2 border-green-500 p-6 rounded-lg">
          <p className="text-lg text-gray-800 leading-relaxed mb-4">
            Based on the comprehensive risk-based workload analysis presented in this report, it is recommended that the organization authorize <strong className="text-green-700">{results.totalFTEs.toFixed(2)} Full-Time Equivalent (FTE)</strong> positions for Environmental Health & Safety operations, representing an annual investment of <strong className="text-green-700">{formatCurrency(results.totalCost)}</strong>.
          </p>
          <p className="text-gray-700 leading-relaxed">
            This staffing level is essential to maintain regulatory compliance, protect organizational assets, ensure employee and{isHospital ? ' patient' : ' stakeholder'} safety, and support sustainable operational performance. The methodology employed represents industry best practices for data-driven staffing decisions and has been validated against {isHospital ? 'healthcare industry ' : ''}benchmarks.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="mb-6">
        <div className="bg-yellow-50 border-2 border-yellow-400 p-6 rounded-lg">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
            <span className="text-2xl mr-2">⚠️</span>
            DISCLAIMER
          </h3>
          <div className="text-sm text-gray-700 leading-relaxed space-y-3">
            <p>
              <strong>This report is for informational and planning purposes only.</strong> The FTE calculations and recommendations are based on the input data, assumptions, and risk factors provided at the time of analysis. Actual staffing requirements may vary based on organizational changes, regulatory updates, incident trends, management system maturity, and other factors not captured in this model.
            </p>
            <p>
              <strong>No Guarantee of Regulatory Compliance:</strong> This analysis does not constitute legal advice and does not guarantee compliance with applicable federal, state, or local regulations. Organizations should consult with qualified legal counsel, regulatory agencies, and EHS professionals to ensure comprehensive compliance with all applicable requirements.
            </p>
            <p>
              <strong>Professional Judgment Required:</strong> Staffing decisions should be made in consultation with qualified EHS professionals who understand the organization's specific operations, risk profile, regulatory environment, and strategic objectives. This model provides a data-driven foundation but should be supplemented with expert judgment and organizational context.
            </p>
            <p>
              <strong>Model Limitations:</strong> The risk-based workload model employs industry-standard methodologies and benchmarks, but individual organizational factors such as facility design, process complexity, hazard exposure levels, incident history, management commitment, and employee safety culture may warrant adjustments to the calculated FTE requirements.
            </p>
            <p>
              <strong>Dynamic Nature of EHS Requirements:</strong> Environmental, health, and safety requirements evolve continuously due to regulatory changes, technological advances, industry developments, and organizational growth. Staffing requirements should be reviewed periodically (recommended annually) and adjusted as needed to maintain program effectiveness.
            </p>
            <p className="font-semibold text-gray-900 mt-4">
              By using this report, you acknowledge that staffing decisions are the sole responsibility of the organization and that the report authors, software developers, and associated parties assume no liability for staffing decisions, compliance outcomes, or organizational consequences resulting from the use of this analysis.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="border-t-2 border-gray-300 pt-6 text-center text-sm text-gray-600">
        <p>EHS Risk-Based Staffing Modeler Pro | {today}</p>
        <p className="mt-2">This is a confidential planning document. Distribution should be limited to authorized personnel.</p>
      </div>
    </div>
  );
};

export default ExecutiveReport;
